<template>
  <div>
    <user-form-modal></user-form-modal>

    <page-heading :title="$t(`${translationPrefix}.title`)" :loading="fetching">
      <template slot="buttons">
        <router-link :to="{ name: 'userCreate' }">
          <el-button type="info">Add New User</el-button>
        </router-link>
      </template>
    </page-heading>

    <content-placeholder v-if="fetching">
      <el-card>
        <p class="text text-75"></p>
        <p class="text text-full"></p>
        <p class="text text-50"></p>
        <p class="text text-50"></p>
        <p class="text text-50"></p>
      </el-card>
    </content-placeholder>

    <div v-else>
      <el-card>
        <div slot="header">
          <el-select v-model="filter" placeholder="Filter by">
            <el-option
              v-for="item in options"
              :key="item.value"
              :label="item.label"
              :value="item.value">
            </el-option>
          </el-select>
        </div>
        <div slot="footer">
          Total: {{data.length}}
        </div>
        <el-table
          :data="data"
          stripe
          :default-sort = "{prop: 'last_name', order: 'ascending'}"
          style="width: 100%"
          :border="true"
          class="depth">
          <el-table-column type="expand">
            <template slot-scope="props">
              <el-row :gutter="20">
                <el-col :span="8">
                  <el-card>
                    <div slot="header">
                      <span>Account Information</span>
                    </div>
                    <table cellpadding="5" cellspacing="5">
                      <tr>
                        <td width="150px"><i class="global-icon fas fa-language"></i> Locale</td>
                        <td>{{ props.row.locale }}</td>
                      </tr>
                      <tr>
                        <td width="150px"><i class="global-icon far fa-globe-americas"></i> Timezone</td>
                        <td>{{ props.row.timezone }}</td>
                      </tr>
                      <tr>
                        <td width="150px"><i class="global-icon fas fa-book-spells"></i> Policy Accepted</td>
                        <td>
                          <span v-if="props.row.policy_accepted_at !== null">
                            {{ props.row.policy_accepted_at | formatTime('MMMM DD, YYYY hh:mm a') }}
                          </span>
                          <span v-else>Not accepted</span>
                        </td>
                      </tr>
                      <tr>
                        <td width="150px"><i class="global-icon fas fa-user-check"></i> Confirmed at</td>
                        <td>{{ props.row.confirmed_at | formatTime('MMMM DD, YYYY hh:mm a') }}</td>
                      </tr>
                      <tr>
                        <td width="150px"><i class="global-icon fas fa-calendar-day"></i> Last login</td>
                        <td>{{ props.row.last_sign_in_at | formatTime('MMMM DD, YYYY hh:mm a') }}</td>
                      </tr>
                    </table>
                  </el-card>

                </el-col>
                <el-col :span="8">

                  <el-card>
                    <div slot="header">
                      <span>Profile Information</span>
                    </div>
                    <table cellpadding="5" cellspacing="5" v-if="props.row.profile">
                      <tr>
                        <td width="150px"><i class="global-icon fas fa-user"></i> Name</td>
                        <td>{{ props.row.profile.first_name }} {{ props.row.profile.last_name }}</td>
                      </tr>
                      <tr v-if="props.row.profile.address || props.row.profile.address2">
                        <td width="150px"><i class="global-icon far fa-map-marker-alt"></i> Address</td>
                        <td>{{ props.row.profile.address }}<br>{{ props.row.profile.address2 }}</td>
                      </tr>
                      <tr v-if="props.row.profile.city || props.row.profile.state || props.row.profile.zip">
                        <td width="150px"><i class="global-icon fas fa-map"></i> City, State, Zip</td>
                        <td>{{ props.row.profile.city }} {{ props.row.profile.state }} {{ props.row.profile.zip }}</td>
                      </tr>
                      <tr v-if="props.row.profile.phone">
                        <td width="150px"><i class="global-icon fas fa-phone"></i> Phone</td>
                        <td>{{ props.row.profile.phone }}</td>
                      </tr>
                      <tr v-if="props.row.profile.title">
                        <td width="150px"><i class="global-icon fas fa-user-tie"></i> Title</td>
                        <td>{{ props.row.profile.title }}</td>
                      </tr>
                      <tr v-if="props.row.profile.starting_date">
                        <td width="150px"><i class="global-icon fas fa-calendar-day"></i> Starting Date</td>
                        <td>{{ props.row.profile.starting_date | formatTime('MMMM DD, YYYY hh:mm a') }}</td>
                      </tr>
                    </table>
                    <el-alert
                      v-else
                      title="A profile is not yet created for this user."
                      type="success"
                      :closable="false">
                    </el-alert>
                  </el-card>
                </el-col>
                <el-col :span="8">
                  <el-card>
                    <div slot="header">
                      <span>App Permissions</span>
                    </div>
                    <div v-if="props.row.user_memberships.length > 0">
                      <table cellpadding="5" cellspacing="5">
                        <tbody v-for="(userMembership, index) in props.row.user_memberships" :key="index">
                          <tr>
                            <td width="150px"><i class="global-icon fas fa-browser"></i> Apps</td>
                            <td>
                              <span>
                                <router-link :to="{ name: 'inviteUsers', params: { id: userMembership.app_id } }">
                                  <el-button type="text">
                                    <slot>{{ app(userMembership.app_id).name }}</slot>
                                  </el-button>
                                </router-link>
                                <el-alert
                                  v-if="userMembership.invitation_accepted_at === null"
                                  :title="`User has been invited but hasn't accepted.`"
                                  type="success"
                                  :closable="false">
                                </el-alert>
                              </span>
                            </td>
                          </tr>
                          <tr>
                            <td width="150px"><i class="global-icon far fa-user-lock"></i> Role</td>
                            <td>{{ role(userMembership.role_id).machine_name }}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    <el-alert
                      v-else
                      title="This user has not been invited to an app."
                      type="success"
                      :closable="false">
                    </el-alert>
                  </el-card>
                </el-col>
              </el-row>
            </template>
          </el-table-column>
          <el-table-column label="" width="58" align="center">
            <template slot-scope="scope">
              <img :src="getGravatar(scope.row.email,'20')" class="thumbnail">
            </template>
          </el-table-column>
          <el-table-column label="First" sortable prop="profile.first_name"></el-table-column>
          <el-table-column label="Last" sortable prop="profile.last_name"></el-table-column>
          <el-table-column prop="email" label="Email" sortable></el-table-column>
          <el-table-column prop="remaining_pto_days" label="PTO/Vacation" sortable></el-table-column>
          <el-table-column prop="remaining_sickness_days" label="Sickness & Wellness" sortable></el-table-column>
          <el-table-column prop="master" label="Master" sortable align="center" width="100" v-if="this.filter == 'all'">
            <template slot-scope="scope">
              <i class="fas fa-user-check" v-if="scope.row.master"></i>
            </template>
          </el-table-column>
          <el-table-column prop="admin" label="Admin" sortable align="center" width="100" v-if="this.filter == 'all'">
            <template slot-scope="scope">
              <i class="fas fa-user-check" v-if="scope.row.admin"></i>
            </template>
          </el-table-column>
          <el-table-column label="" width="160">
            <template slot-scope="scope">
              <router-link :to="{ name: 'userProfile', params: { id: scope.row.id } }">
                <el-tooltip effect="dark" content="Go to user's page" placement="bottom">
                  <el-button size="mini" type="primary"><i class="fas fa-search"></i></el-button>
                </el-tooltip>
              </router-link>
              <el-tooltip effect="dark" content="Edit account details" placement="bottom">
                <el-button size="mini" plain @click="handleOpenEditUserModal(scope.row)"><i class="fas fa-pen"></i></el-button>
              </el-tooltip>
              <el-tooltip effect="dark" content="Disable user" placement="bottom" v-if="!scope.row.deactivated">
                <el-button size="mini" plain @click="deactivateUser(scope.row.id)" v-if="!scope.row.master"><i class="fas fa-times"></i></el-button>
              </el-tooltip>
              <el-tooltip effect="dark" content="Activate user" placement="bottom" v-if="!!scope.row.deactivated" :key="scope.row.id" >
                <el-button size="mini" plain @click="activateUser(scope.row.id)"><i class="far fa-check"></i></el-button>
              </el-tooltip>
            </template>
          </el-table-column>
        </el-table>

      </el-card>
    </div>
  </div>
</template>

<script>
  import avatar from '@/mixins/avatar';
  import { mapActions, mapGetters } from 'vuex';

  import UserFormModal from '@/components/user/UserFormModal';
  import { showSuccessMessage, showErrorMessage } from '@/ui/message';

  export default {
    name: 'UsersPage',
    mixins: [avatar],
    created() {
      this.fetchUsers();
      this.fetchApps();
    },
    components: {
      userFormModal: UserFormModal,
    },
    data() {
      return {
        translationPrefix: 'pages.users',
        options: [{
          value: 'all',
          label: 'All',
        }, {
          value: 'employees',
          label: 'Employees',
        }, {
          value: 'admins',
          label: 'Admins',
        }, {
          value: 'active',
          label: 'Active',
        }, {
          value: 'unconfirmed',
          label: 'Unconfirmed',
        }, {
          value: 'disabled',
          label: 'Disabled',
        }],
        filter: 'active',
      };
    },
    computed: {
      ...mapGetters('entities/users', ['fetching']),
      ...mapGetters('entities/companies', ['isAppSubscribed']),

      all() {
        return this.$store.getters['entities/users/query']()
          .with('profile')
          .with('user_memberships')
          .get();
      },
      admins() {
        return this.$store.getters['entities/users/query']()
          .where('admin', true)
          .with('profile')
          .get();
      },
      users() {
        return this.$store.getters['entities/users/query']()
          .where('admin', false)
          .where('status', 0)
          .with('profile')
          .with('user_memberships')
          .get();
      },
      unconfirmed() {
        return this.$store.getters['entities/users/query']()
          .with('profile')
          .with('user_memberships')
          .where('confirmed_at', null)
          .get();
      },
      active() {
        return this.$store.getters['entities/users/query']()
          .where(record => !record.deactivated)
          .with('profile')
          .with('user_memberships')
          .get();
      },
      disabled() {
        return this.$store.getters['entities/users/query']()
          .where('deactivated', true)
          .with('profile')
          .with('user_memberships')
          .get();
      },
      apps() {
        return this.$store.getters['entities/apps/query']()
          .with('packages')
          .get();
      },
      data() {
        switch (this.filter) {
          case 'admins':
            return this.admins;
          case 'unconfirmed':
            return this.unconfirmed;
          case 'active':
            return this.active;
          case 'disabled':
            return this.disabled;
          case 'all':
            return this.all;
          default:
            return this.users;
        }
      },

    },
    methods: {
      ...mapActions('entities/users', ['fetchUsers', 'openFormModal', 'deleteUser', 'updateUser']),
      ...mapActions('entities/apps', ['fetchApps']),
      ...mapActions('entities/policies', ['acceptPolicy']),

      handleOpenUserModal() {
        this.openFormModal();
      },
      handleOpenEditUserModal(user) {
        this.openFormModal({ userId: user.id });
      },
      isCompanySubscribed(appId) {
        return this.isAppSubscribed(appId);
      },
      app(id) {
        return this.$store.getters['entities/apps/query']()
          .where('id', id)
          .first();
      },
      role(id) {
        return this.$store.getters['entities/roles/query']()
          .where('id', id)
          .first();
      },
      activateUser(id) {
        const self = this;
        this.$confirm('Are you sure you want to activate this user?', {
          confirmButtonText: 'OK',
          cancelButtonText: 'Cancel',
          title: 'Confirm',
        }).then(() => {
          self.updateUser({ id, data: { user: { deactivated: false } } })
            .then((response) => {
              self.$forceUpdate();
              if (response.status) {
                showSuccessMessage('User activated successfully');
              } else {
                showErrorMessage(response.error);
              }
            });
        });
      },
      deactivateUser(id) {
        const self = this;
        this.$confirm('Are you sure you want to deactivate this user?', {
          confirmButtonText: 'OK',
          cancelButtonText: 'Cancel',
          title: 'Confirm',
        }).then(() => {
          self.deleteUser(id)
            .then((response) => {
              if (response.status) {
                showSuccessMessage('User deactivated successfully');
              } else {
                showErrorMessage(response.error);
              }
            });
        });
      },
    },
  };
</script>
<style lang="scss" scoped>
.el-button{
  margin-left: 0px;
}
</style>



// WEBPACK FOOTER //
// src/pages/Users.vue